import { useEffect, useCallback, useRef } from 'react';
import { ENV_CONFIG } from '../../shared/config/env.config';
import { alphabetToNumber } from '../../shared/services/userSessionService';

/**
 * Manages the rotating `AuthorizationKey` that every API call sends.
 *
 * Until the login page was migrated this hook also seeded a hardcoded `token`
 * and `DOF-<apiBaseUrl>-AuthData` pair into localStorage so the dashboard had a
 * session to work with. Login owns those two keys now (see `login.tsx` ->
 * `storeSession()`), so they are no longer written here - seeding them on every
 * render would overwrite the real session on the next reload.
 */

export const useAuthKey = () => {
  // Guards against overlapping auth-key fetches. Multiple triggers (mount,
  // the 3s validity interval, the online event, StrictMode's double-mount in
  // dev) can all call manualRefetch at once; this collapses them into one
  // in-flight request.
  const isFetchingRef = useRef(false);

  /**
   * Store Authorization key in localStorage
   */
  const storeAuthKey = useCallback((result?: any) => {
    if (!result?.Authorization_key) return;

    // const parsed =
    //   typeof result.body === 'string'
    //     ? JSON.parse(result.body || '{}')
    //     : result.body;

    const key = result?.Authorization_key;

    if (key) {
      localStorage.setItem('AuthorizationKey', key);
      //console.log('Authorization Key stored:', key);
    }
  }, []);

  /**
   * Fetch a fresh Authorization key
   */
  const manualRefetch = useCallback(async () => {
    // Skip if a fetch is already running so we don't fire duplicate requests.
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    try {
      const response = await fetch(
        // Was ENV_CONFIG.getSignedUrlApi, which is the S3 pre-signed upload
        // service - it never returns an Authorization_key.
        ENV_CONFIG.authKeyApi,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json, text/plain, */*',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();

      storeAuthKey(result);
    } catch (err) {
      console.error('Auth key fetch failed:', err);
    } finally {
      isFetchingRef.current = false;
    }
  }, [storeAuthKey]);

  /**
   * Check whether stored Authorization key is valid or expired
   */
  const checkKeyValidTime = useCallback(() => {
    const key = localStorage.getItem('AuthorizationKey');

    // The key can disappear without a page reload (e.g. logout runs
    // localStorage.clear(), or the 401 handler in baseQuery clears storage).
    // Since useAuthKey is mounted once in App and never remounts, the mount
    // effect won't re-run to reseed it, so refetch here instead of bailing.
    if (!key) {
      manualRefetch();
      return;
    }

    const inputStr = key.toString();

    const first6 = inputStr.slice(0, 6);
    const last6 = inputStr.slice(-6);
    const middle = inputStr.slice(6, -6);

    /**
     * Same as Angular:
     * const test = this.commonService.alphabetToNumber(middle);
     */
    const test = alphabetToNumber(middle);

    const timestamp = parseInt(test, 10);

    // A malformed key (prefix/suffix mismatch, or a middle that doesn't decode
    // to a numeric timestamp) is invalid — refetch. Without the NaN guard an
    // unparseable middle yields an Invalid Date, and `InvalidDate < now` is
    // always false, so a bad key would slip through as "valid".
    if (first6 !== last6 || Number.isNaN(timestamp)) {
      manualRefetch();
      return;
    }

    const dateexpiresObj = new Date(timestamp);
    const currentDate = new Date();

    if (dateexpiresObj < currentDate) {
      //console.log('Authorization key expired → refetching...');
      manualRefetch();
    }
  }, [manualRefetch]);

  /**
   * On mount:
   * If Authorization is missing, fetch new key.
   * If key exists, validate it.
   */
  useEffect(() => {
    const existingKey = localStorage.getItem('AuthorizationKey');
    if (!existingKey) {
      //console.log('No stored Authorization key → fetching...');
      manualRefetch();
    } else {
      checkKeyValidTime();
    }
  }, [manualRefetch, checkKeyValidTime]);

  /**
   * Network online/offline events
   */
  useEffect(() => {
    const handleOnline = () => {
      //console.log('Network Online...');
      checkKeyValidTime();
    };

    const handleOffline = () => {
      console.log('Network Offline...');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [checkKeyValidTime]);

  /**
   * Check key validity every 3 seconds
   */
  useEffect(() => {
    const interval = setInterval(() => {
      // Always run the check: checkKeyValidTime now refetches when the key is
      // missing (e.g. right after a logout that cleared localStorage), so it
      // must not be gated on the key still being present.
      checkKeyValidTime();
    }, 3000);

    return () => clearInterval(interval);
  }, [checkKeyValidTime]);

  /**
   * Optional full refresh every 55 minutes
   */
  useEffect(() => {
    const interval = setInterval(() => {
      //console.log('Scheduled Authorization key refresh...');
      manualRefetch();
    }, 3300000);

    return () => clearInterval(interval);
  }, [manualRefetch]);

  return {
    manualRefetch,
    checkKeyValidTime,
  };
};
